import React, { useState, useEffect } from 'react';
import { Tag, Button, Table, Popconfirm, message } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import type { TableProps } from 'antd';
import { cloneDeep } from 'lodash';

import { getDiffCityPRCarLineList, diffCityPRCarLineUpdateStatus } from 'hooks/carRental/store';

import emiter, {
	EVENT_CARRENTAL_ADD_REMOTERETURN,
	EVENT_CARRENTAL_EDIT_REMOTERETURN,
	EVENT_CARRENTAL_DIFF_PR_CAR_SEARCH,
} from '../../../../events';

import {
	ITableStatusPropsType,
	IRemoteReturnTableDataType,
	IRemoteReturnTableActionBarPropsType,
	IRemoteReturnTablePropsType,
} from '../../../../types/carRental';
import SPButton from "../../base/SPButton";

// import { planStatusColor } from '../../color'

const DEFAULT_PAGE_SIZE = 10;

const SPDifCityRemoteReturnActionBar: React.FC<any> = (props) => {
	const { enums, permission } = props;
	const createButtonText = '新增路线';
	const actionBarStyle: React.CSSProperties = { marginBottom: '12px', display: 'flex', justifyContent: 'right' };

	const onCreate = () => {
		emiter.emit(EVENT_CARRENTAL_ADD_REMOTERETURN);
	};

	return (
		<div style={actionBarStyle}>
			<SPButton permission={permission} permissionKey='store_diff_city_pr_car_line_add' type="primary" onClick={onCreate}>
				{createButtonText}
			</SPButton>
		</div>
	);
};

const SPDifCityRemoteReturnTableStatus: React.FC<ITableStatusPropsType> = props => {
	const { enums, value } = props;

	// TODO: 加入到枚举值
	let t = { text: '未知标签', color: 'default' };

	// for (let key in enums)
	//   if(key == value) t = { text: enums[key].value, color: planStatusColor.tagColor[parseInt(key)] }

	return <Tag color={t.color}>{t.text}</Tag>;
};

const SPDifCityRemoteReturnTableActionBar: React.FC<IRemoteReturnTableActionBarPropsType> = props => {
	const { data: rowData, permission, updateList } = props;

	//从enums删除本身已有状态
	//const newEnums = enums?.filter((item: any) => item.key !== data.packageStatus) || []

	const onEdit = () => {
		emiter.emit(EVENT_CARRENTAL_EDIT_REMOTERETURN, rowData);
	};

	const onDisabled = (record: any) => {
		// emiter.emit(EVENT_TRAVEL_VIEW_PLAN, data);
		diffCityPRCarLineUpdateStatus({ diff_city_pr_car_line_code: record.diff_city_pr_car_line_code }).then(
			(res: any) => {
				const { errno } = res || {};
				if (errno === 0) {
					message.success('操作成功');
					updateList && updateList();
				}
			}
		);
	};

	return (
		<>
			<SPButton permissionKey='store_diff_city_pr_car_line_update' permission={permission} type="link" onClick={onEdit}>
				修改
			</SPButton>
			<Popconfirm
				title={`确认${rowData.status === 1 ? '禁用' : '启用'}路线吗`}
				onConfirm={() => onDisabled(rowData)}
				okText="确认"
				cancelText="取消"
			>
				<SPButton permissionKey='store_diff_city_pr_car_line_update_status' permission={permission} type="link">{rowData.status === 1 ? '禁用' : '启用'}</SPButton>
			</Popconfirm>
		</>
	);
};

const SPDifCityRemoteReturnTable: React.FC<IRemoteReturnTablePropsType> = props => {
	const { enums, permission } = props;

	const { diff_city_pr_car_line_status } = enums;

	const columns: TableProps<IRemoteReturnTableDataType>['columns'] = [
		{ title: '取车城市', dataIndex: 'pickup_city_name', key: 'pickup_city_name' },
		{ title: '还车城市', dataIndex: 'return_city_name', key: 'return_city_name' },
		{ title: '跨城还车服务费', dataIndex: 'service_fee', key: 'service_fee' },
		{ title: '库存占用时间(小时)', dataIndex: 'stock_occupy_time', key: 'stock_occupy_time' },
		{
			title: '状态',
			dataIndex: 'status',
			key: 'status',
			render: status => (diff_city_pr_car_line_status || []).find((item: any) => item.key === status)?.value,
		},
		{
			title: '操作',
			key: 'action',
			fixed: 'right',
			className: 'common-tab-action-bar',
			render: (_, record) => <SPDifCityRemoteReturnTableActionBar permission={permission} data={record} updateList={() => loadData(currentPageData)} />,
		},
	];

	const [data, setData] = useState([]);
	const [searchData, setSearchData] = useState({});
	const [totalCount, setTotalCount] = useState(0);
	const [currentPageData, setCurrentPageData] = useState<any>({ pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE });
	const [cityList, setCityList] = useState([]);

	const loadData = (data: any) => {
		const { pageIndex = 1, pageSize = DEFAULT_PAGE_SIZE } = data || {};
		const params = {
			...data,
			page: pageIndex,
			page_size: pageSize,
		};
		getDiffCityPRCarLineList(params).then(res => {
			const { list, page, page_size, total } = res?.data || {};
			(list || []).forEach((item: any) => {
				item.service_fee = item.service_fee / 100;
			})
			setTotalCount(total);
			setData(list);
			setCurrentPageData({ pageIndex: page, pageSize: page_size });
		});
	};

	// TODO: 清空再搜索，翻页有问题
	const onPaginationChange = (pagination: TablePaginationConfig) => {
		setCurrentPageData({
			pageIndex: pagination.current,
			pageSize: pagination.pageSize,
		});
		loadData({ pageIndex: pagination.current, pageSize: pagination.pageSize });
		// emiter.emit(EVENT_TRAVEL_SEARCH_PLAN, Object.assign(searchData, {
		//     pageIndex: pagination.current,
		//     pageSize: pagination.pageSize
		// }));
	};

	useEffect(() => {
		loadData(currentPageData);
	}, []);

	useEffect(() => {
		emiter.on(EVENT_CARRENTAL_DIFF_PR_CAR_SEARCH, loadData);
		return () => {
			emiter.removeAllListeners(EVENT_CARRENTAL_DIFF_PR_CAR_SEARCH)
		}
	}, []);

	return (
		<>
			<SPDifCityRemoteReturnActionBar permission={permission} />
			<Table
				size='middle'
				columns={columns}
				dataSource={data}
				rowKey={cols => cols.id}
				pagination={{
					total: totalCount,
					showSizeChanger: true,
					showQuickJumper: true,
					showTotal: total => `共 ${total} 条数据`,
					current: currentPageData?.pageIndex,
					pageSize: currentPageData?.pageSize,
				}}
				onChange={onPaginationChange}
			/>
		</>
	);
};

export default SPDifCityRemoteReturnTable;
